import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import {
  Button,
  Card,
  Container,
  Icon,
  MoodChip,
  OwlMark,
  moodById,
} from "@wiseowl/design-system";
import { ReflectFlow } from "../components/reflect-flow.js";
import { isEmbedded, openAppPath } from "../lib/embed.js";
import {
  getName,
  getTodayReflection,
  hasToken,
  type AnonReflection,
} from "../lib/anon-reflection.js";

/*
 * Landing — the anonymous daily reflection. The homepage IS the product:
 * a visitor reflects on the device before they ever see a sign-up form.
 *
 * State machine (doc 13 §7.3 / §10.1):
 *   logged in            → /home
 *   reflected today      → read-only result + sign-up-to-save
 *   otherwise            → inline ReflectFlow (anon, device-local)
 */
export const Landing = () => {
  const navigate = useNavigate();
  const [reflection, setReflection] = useState<AnonReflection | null>(() => getTodayReflection());

  if (hasToken()) return <Navigate to="/home" replace />;

  const goSignUp = () => openAppPath("/sign-up", navigate);
  const goSignIn = () => openAppPath("/sign-in", navigate);

  return (
    <>
      <section className={isEmbedded() ? "" : "border-b border-line"}>
        <Container className={isEmbedded() ? "py-2 md:py-3" : "py-8 md:py-12"}>
          <div className={isEmbedded() ? "mx-auto max-w-[680px]" : "mx-auto max-w-5xl"}>
            {isEmbedded() ? (
              <p className="mb-5 text-center text-muted">
                90 seconds. No sign-up. Just you and one honest question.
              </p>
            ) : (
<div className="mb-5 text-center">
              <div className="mb-3 inline-flex items-center gap-2 rounded-pill border border-line bg-paper px-3 py-1.5 font-mono text-eyebrow font-semibold uppercase tracking-wider text-teal">
                <Icon name="spark" size={14} className="text-mustard" />
                A 90-second reset
              </div>
              <h1 className="font-serif text-[clamp(26px,4vw,40px)] font-medium leading-[1.08] tracking-tight text-ink text-pretty">
                A wise pause in a noisy world.
              </h1>
              <p className="mt-2 text-muted">
                90 seconds. No sign-up. Just you and one honest question.
              </p>
            </div>
            )}

            {reflection ? (
              <ResultView reflection={reflection} onSignUp={goSignUp} onSignIn={goSignIn} />
            ) : (
              <ReflectFlow
                mode="anon"
                presentation="inline"
                personaName={getName()}
                onSignUpToSave={goSignUp}
                onSignIn={goSignIn}
                onComplete={() => setReflection(getTodayReflection())}
              />
            )}
          </div>
        </Container>
      </section>

      {/* What it is — visible on one short scroll (hidden when embedded) */}
      {!isEmbedded() && (
      <section>
        <Container className="grid gap-4 py-10 md:grid-cols-3 md:py-14">
          <Card>
            <div className="grid h-10 w-10 place-items-center rounded-md bg-teal/[0.08] text-teal">
              <Icon name="leaf" size={22} />
            </div>
            <h3 className="mt-3 font-serif text-h3 font-medium text-ink">Pause Daily</h3>
            <p className="mt-1.5 text-small text-muted">
              No pressure. No perfect words. Take a short daily pause to notice how you&apos;re feeling, reflect on your day, and check in with yourself.
            </p>
          </Card>
          <Card>
            <div className="grid h-10 w-10 place-items-center rounded-md bg-mustard-soft text-mustard">
              <Icon name="chart" size={22} />
            </div>
            <h3 className="mt-3 font-serif text-h3 font-medium text-ink">Discover your patterns</h3>
            <p className="mt-1.5 text-small text-muted">
              Over 14 days, Wise Owl Talks quietly learns from your reflections. See recurring emotions, triggers, habits, and themes that are easy to miss day to day.
            </p>
          </Card>
          <Card>
            <div className="grid h-10 w-10 place-items-center rounded-md bg-teal/[0.08] text-teal">
              <Icon name="spark" size={22} />
            </div>
            <h3 className="mt-3 font-serif text-h3 font-medium text-ink">Know your next step</h3>
            <p className="mt-1.5 text-small text-muted">
              Your personal dashboard helps you understand what&apos;s happening beneath the surface. If you need more support, we&apos;ll recommend the right course or a 1:1 conversation with a coach.
            </p>
          </Card>
        </Container>
      </section>
      )}
    </>
  );
};

/** Read-only view of today's device reflection (the day gate is closed). */
const ResultView = ({
  reflection,
  onSignUp,
  onSignIn,
}: {
  reflection: AnonReflection;
  onSignUp: () => void;
  onSignIn: () => void;
}) => {
  const mood = moodById(reflection.moodSlug);
  const { insight, name } = reflection;
  return (
    <Card surface="paper" className={`!p-5 md:!p-7 ${isEmbedded() ? "shadow-2" : ""}`}>
      <div className="mb-3.5 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2.5">
          <OwlMark size={32} />
          <div className="font-serif text-small italic text-ink-soft">
            {name ? `${name}, here's what you noticed today` : "What you noticed today"}
          </div>
        </div>
        {mood && <MoodChip mood={mood} />}
      </div>

      <p className="font-serif text-h2 font-medium leading-snug tracking-tight text-ink text-pretty">
        &ldquo;{insight.acknowledgement}&rdquo;
      </p>

      <Card surface="cream-2" className="mt-5">
        <div className="font-mono text-eyebrow font-semibold uppercase tracking-wider text-teal">
          A gentle insight
        </div>
        <p className="mt-2 font-serif text-h3 leading-relaxed text-ink">{insight.insight}</p>
      </Card>

      <Card surface="ink" className="mt-4">
        <div className="flex items-center gap-1.5 font-mono text-eyebrow font-semibold uppercase tracking-wider text-mustard">
          <Icon name="leaf" size={16} /> One tiny action · today
        </div>
        <p className="mt-2.5 font-serif text-h3 leading-relaxed text-cream">{insight.tinyAction}</p>
      </Card>

      <Card surface="cream-2" className="mt-5 !bg-mustard-soft">
        <p className="text-small text-ink">
          This reflection lives only on your device. Sign up to save it and build a personal mood bank.
        </p>
      </Card>
      <div className="mt-3 flex flex-col gap-2.5 sm:flex-row">
        <Button variant="mustard" fullWidth onClick={onSignUp}>Sign up to save</Button>
      </div>
      <p className="mt-3 text-center text-small text-muted">
        Already have an account?{" "}
        <button type="button" onClick={onSignIn} className="font-medium text-teal hover:underline">Sign in</button>
      </p>
      <p className="mt-3 text-center text-meta text-muted">A new reflection opens tomorrow.</p>
    </Card>
  );
};
